import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 20, bottom: 50, left: 60 };
const BAR_MARGIN = { top: 20, right: 30, bottom: 30, left: 50 };

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  toggleGroup: {
    display: 'flex',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    overflow: 'hidden',
    flexShrink: 0,
    marginLeft: '16px',
  },
  toggleButton: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s',
  },
  toggleActive: {
    background: '#2563eb',
    color: '#fff',
  },
  toggleInactive: {
    background: '#fff',
    color: '#6b7280',
  },
  kpiRow: {
    display: 'flex',
    gap: '24px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  kpiCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  kpiLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    fontWeight: 500,
  },
  kpiValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1f2937',
  },
  kpiTrend: {
    fontSize: '11px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
  },
  viewToggleRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  viewButton: {
    padding: '5px 14px',
    fontSize: '12px',
    fontWeight: 500,
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    cursor: 'pointer',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  },
  viewButtonActive: {
    background: '#2563eb',
    color: '#fff',
    borderColor: '#2563eb',
  },
  viewButtonInactive: {
    background: '#fff',
    color: '#6b7280',
    borderColor: '#e5e7eb',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function computeTrend(values) {
  if (!values || values.length < 2) return null;
  const n = values.length;
  const recent = values[n - 1];
  const earlier = values[0];
  const diff = recent - earlier;
  if (Math.abs(diff) < 0.01) return 'stable';
  return diff > 0 ? 'widening' : 'narrowing';
}

export default function PriceGapChart({ data }) {
  const timelineSvgRef = useRef(null);
  const stateSvgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [view, setView] = useState('timeline');
  const [mode, setMode] = useState('dollar');

  const sortedStates = useMemo(() => {
    if (!data || !data.byState || data.byState.length === 0) return [];
    const sorted = [...data.byState];
    if (mode === 'dollar') {
      sorted.sort((a, b) => b.avgGap - a.avgGap);
    } else {
      sorted.sort((a, b) => b.avgGapPercent - a.avgGapPercent);
    }
    return sorted;
  }, [data, mode]);

  const trend = useMemo(() => {
    if (!data || !data.timeline) return null;
    const values = mode === 'dollar' ? data.timeline.avgGap : data.timeline.avgGapPercent;
    return computeTrend(values);
  }, [data, mode]);

  // Timeline view rendering
  useEffect(() => {
    if (view !== 'timeline') return;
    if (!data || !data.timeline || !data.timeline.dates || data.timeline.dates.length === 0) return;

    const svg = d3.select(timelineSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const tl = data.timeline;
    const dates = tl.dates;
    const n = dates.length;

    const avgValues = mode === 'dollar' ? tl.avgGap : tl.avgGapPercent;
    const minValues = mode === 'dollar' ? tl.minGap : tl.minGap.map((v, i) => {
      // Approximate min gap percent from min gap dollar using avgGap ratio
      if (tl.avgGap[i] === 0) return 0;
      return (v / tl.avgGap[i]) * tl.avgGapPercent[i];
    });
    const maxValues = mode === 'dollar' ? tl.maxGap : tl.maxGap.map((v, i) => {
      if (tl.avgGap[i] === 0) return 0;
      return (v / tl.avgGap[i]) * tl.avgGapPercent[i];
    });

    // Collect all values for Y domain
    const allValues = [
      ...minValues,
      ...maxValues,
      ...avgValues,
    ].filter((v) => v != null);

    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPadding = (yMax - yMin) * 0.1 || 1;

    // Scales
    const x = d3.scalePoint()
      .domain(dates)
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([Math.max(0, yMin - yPadding), yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Min-max range area fill
    const areaData = dates.map((date, i) => ({
      date,
      min: minValues[i],
      max: maxValues[i],
    }));

    const areaGen = d3.area()
      .x((d) => x(d.date))
      .y0((d) => y(d.min))
      .y1((d) => y(d.max))
      .defined((d) => d.min != null && d.max != null);

    g.append('path')
      .datum(areaData)
      .attr('d', areaGen)
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.1)
      .attr('stroke', 'none');

    // Min line (dashed)
    const minLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(minValues)
      .attr('d', minLine)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Max line (dashed)
    const maxLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(maxValues)
      .attr('d', maxLine)
      .attr('fill', 'none')
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3');

    // Average gap line (solid)
    const avgLine = d3.line()
      .x((d, i) => x(dates[i]))
      .y((d) => y(d))
      .defined((d) => d != null);

    g.append('path')
      .datum(avgValues)
      .attr('d', avgLine)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Dots on average line
    avgValues.forEach((v, i) => {
      if (v == null) return;
      g.append('circle')
        .attr('cx', x(dates[i]))
        .attr('cy', y(v))
        .attr('r', 3)
        .attr('fill', '#2563eb');
    });

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => formatDateLabel(d)))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    g.select('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => mode === 'dollar' ? `$${v.toFixed(2)}` : `${v.toFixed(1)}%`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Crosshair line (initially hidden)
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    // Invisible overlay columns for tooltip hover
    const colWidth = n > 1 ? innerW / (n - 1) : innerW;

    dates.forEach((date, i) => {
      const cx = x(date);
      const halfW = colWidth / 2;

      g.append('rect')
        .attr('x', cx - halfW)
        .attr('y', 0)
        .attr('width', colWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'timeline',
            date,
            avgGap: tl.avgGap[i],
            avgGapPercent: tl.avgGapPercent[i],
            minGap: tl.minGap[i],
            maxGap: tl.maxGap[i],
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, view, mode]);

  // State view rendering
  useEffect(() => {
    if (view !== 'state') return;
    if (!sortedStates || sortedStates.length === 0) return;

    const svg = d3.select(stateSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 600;
    const barHeight = 24;
    const barGap = 6;
    const chartHeight = Math.max(
      CHART_HEIGHT,
      BAR_MARGIN.top + BAR_MARGIN.bottom + sortedStates.length * (barHeight + barGap)
    );
    const width = Math.max(600, containerWidth);
    const innerW = width - BAR_MARGIN.left - BAR_MARGIN.right;
    const innerH = chartHeight - BAR_MARGIN.top - BAR_MARGIN.bottom;

    svg.attr('width', width).attr('height', chartHeight);

    const g = svg.append('g').attr('transform', `translate(${BAR_MARGIN.left}, ${BAR_MARGIN.top})`);

    const values = sortedStates.map((d) => mode === 'dollar' ? d.avgGap : d.avgGapPercent);
    const xMax = d3.max(values) || 1;
    const xPadding = xMax * 0.15;

    const overallAvg = mode === 'dollar'
      ? (data.overallAvgGap || 0)
      : (data.overallAvgGapPercent || 0);

    // Scales
    const x = d3.scaleLinear()
      .domain([0, xMax + xPadding])
      .range([0, innerW]);

    const y = d3.scaleBand()
      .domain(sortedStates.map((d) => d.state))
      .range([0, innerH])
      .padding(0.2);

    // Vertical grid lines
    const xTicks = x.ticks(6);
    g.selectAll('.grid-line')
      .data(xTicks)
      .enter()
      .append('line')
      .attr('x1', (d) => x(d))
      .attr('x2', (d) => x(d))
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Overall average reference line
    g.append('line')
      .attr('x1', x(overallAvg))
      .attr('x2', x(overallAvg))
      .attr('y1', -10)
      .attr('y2', innerH + 5)
      .attr('stroke', '#dc2626')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3');

    // Reference line label
    g.append('text')
      .attr('x', x(overallAvg))
      .attr('y', -14)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#dc2626')
      .attr('font-weight', 600)
      .text(mode === 'dollar' ? `Avg $${overallAvg.toFixed(2)}` : `Avg ${overallAvg.toFixed(1)}%`);

    // Bars
    sortedStates.forEach((d) => {
      const val = mode === 'dollar' ? d.avgGap : d.avgGapPercent;
      const barW = x(val);
      const barY = y(d.state);
      const bh = y.bandwidth();

      if (barW <= 0) return;

      g.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', bh)
        .attr('fill', '#2563eb')
        .attr('fill-opacity', 0.75)
        .attr('rx', 2)
        .attr('ry', 2)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          d3.select(event.currentTarget).attr('fill-opacity', 1);
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'state',
            state: d.state,
            avgGap: d.avgGap,
            avgGapPercent: d.avgGapPercent,
            addressCount: d.addressCount,
          });
        })
        .on('mouseleave', (event) => {
          d3.select(event.currentTarget).attr('fill-opacity', 0.75);
          setTooltip(null);
        });

      // Value label at end of bar
      g.append('text')
        .attr('x', barW + 6)
        .attr('y', barY + bh / 2)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('fill', '#374151')
        .attr('font-weight', 600)
        .text(mode === 'dollar' ? `$${val.toFixed(2)}` : `${val.toFixed(1)}%`);
    });

    // Y axis (state labels)
    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '11px')
      .attr('fill', '#374151')
      .attr('font-weight', 500);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => mode === 'dollar' ? `$${v}` : `${v}%`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data, sortedStates, view, mode]);

  // Empty state
  if (!data || (!data.timeline && !data.byState)) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.headerText}>
            <div style={styles.title}>Premium Price Gap Analysis</div>
            <div style={styles.subtitle}>Spread between Pest365 and Pest365+ tiers over time</div>
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No price gap data available
        </div>
      </div>
    );
  }

  const currentAvgGap = data.overallAvgGap != null ? data.overallAvgGap : null;
  const currentAvgGapPct = data.overallAvgGapPercent != null ? data.overallAvgGapPercent : null;

  const trendArrow = trend === 'widening' ? '\u2191' : trend === 'narrowing' ? '\u2193' : '\u2192';
  const trendColor = trend === 'widening' ? '#dc2626' : trend === 'narrowing' ? '#16a34a' : '#6b7280';
  const trendLabel = trend === 'widening' ? 'Widening' : trend === 'narrowing' ? 'Narrowing' : 'Stable';

  return (
    <div style={styles.card}>
      {/* Header with title and dollar/percent toggle */}
      <div style={styles.header}>
        <div style={styles.headerText}>
          <div style={styles.title}>Premium Price Gap Analysis</div>
          <div style={styles.subtitle}>Spread between Pest365 and Pest365+ tiers over time</div>
        </div>
        <div style={styles.toggleGroup}>
          <button
            style={{
              ...styles.toggleButton,
              ...(mode === 'dollar' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setMode('dollar')}
          >
            $ Gap
          </button>
          <button
            style={{
              ...styles.toggleButton,
              ...(mode === 'percent' ? styles.toggleActive : styles.toggleInactive),
            }}
            onClick={() => setMode('percent')}
          >
            % Gap
          </button>
        </div>
      </div>

      {/* KPI summary */}
      <div style={styles.kpiRow}>
        {currentAvgGap != null && (
          <div style={styles.kpiCard}>
            <span style={styles.kpiLabel}>Avg Gap ($)</span>
            <span style={styles.kpiValue}>${currentAvgGap.toFixed(2)}</span>
          </div>
        )}
        {currentAvgGapPct != null && (
          <div style={styles.kpiCard}>
            <span style={styles.kpiLabel}>Avg Gap (%)</span>
            <span style={styles.kpiValue}>{currentAvgGapPct.toFixed(1)}%</span>
          </div>
        )}
        {trend && (
          <div style={styles.kpiCard}>
            <span style={styles.kpiLabel}>Trend</span>
            <span style={{ ...styles.kpiTrend, color: trendColor }}>
              {trendArrow} {trendLabel}
            </span>
          </div>
        )}
      </div>

      {/* View toggle buttons */}
      <div style={styles.viewToggleRow}>
        <button
          style={{
            ...styles.viewButton,
            ...(view === 'timeline' ? styles.viewButtonActive : styles.viewButtonInactive),
          }}
          onClick={() => setView('timeline')}
        >
          Timeline
        </button>
        <button
          style={{
            ...styles.viewButton,
            ...(view === 'state' ? styles.viewButtonActive : styles.viewButtonInactive),
          }}
          onClick={() => setView('state')}
        >
          By State
        </button>
      </div>

      {/* Timeline legend */}
      {view === 'timeline' && (
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
            </svg>
            <span>Avg Gap</span>
          </div>
          <div style={styles.legendItem}>
            <svg width="20" height="10">
              <rect x="0" y="0" width="20" height="10" fill="#2563eb" fillOpacity="0.1" rx="2" />
            </svg>
            <span>Min-Max Range</span>
          </div>
          <div style={styles.legendItem}>
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="#d1d5db" strokeWidth="1" strokeDasharray="4,3" />
            </svg>
            <span>Min / Max</span>
          </div>
        </div>
      )}

      {/* State legend */}
      {view === 'state' && (
        <div style={styles.legend}>
          <div style={styles.legendItem}>
            <svg width="14" height="10">
              <rect x="0" y="0" width="14" height="10" fill="#2563eb" fillOpacity="0.75" rx="2" />
            </svg>
            <span>{mode === 'dollar' ? 'Avg Gap ($)' : 'Avg Gap (%)'}</span>
          </div>
          <div style={styles.legendItem}>
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="#dc2626" strokeWidth="1.5" strokeDasharray="6,3" />
            </svg>
            <span>Overall Average</span>
          </div>
        </div>
      )}

      <div style={styles.container} ref={containerRef}>
        {view === 'timeline' && <svg ref={timelineSvgRef} />}
        {view === 'state' && <svg ref={stateSvgRef} />}
      </div>

      {/* Tooltip */}
      {tooltip && tooltip.type === 'timeline' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb', fontWeight: 600 }}>Avg Gap</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.avgGap != null ? tooltip.avgGap.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#8b5cf6' }}>Avg Gap %</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.avgGapPercent != null ? tooltip.avgGapPercent.toFixed(1) : '--'}%
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#9ca3af' }}>Min Gap</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.minGap != null ? tooltip.minGap.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#9ca3af' }}>Max Gap</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.maxGap != null ? tooltip.maxGap.toFixed(2) : '--'}
            </span>
          </div>
        </div>
      )}

      {tooltip && tooltip.type === 'state' && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.state}
          </div>
          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px' }}>
            {tooltip.addressCount} address{tooltip.addressCount !== 1 ? 'es' : ''}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#2563eb' }}>Avg Gap ($)</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.avgGap != null ? tooltip.avgGap.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#8b5cf6' }}>Avg Gap (%)</span>
            <span style={{ fontWeight: 600 }}>
              {tooltip.avgGapPercent != null ? tooltip.avgGapPercent.toFixed(1) : '--'}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
